/** Usage-unit tolerance when comparing summed interval usage to the Usage Amount. */
const INTERVAL_USAGE_TOLERANCE = 0.01;

export interface IntervalUsageFacts {
  onPeakUsage: number;
  onPeakCost: number;
  intermediateUsage: number;
  intermediateCost: number;
  offPeakUsage: number;
  offPeakCost: number;
}

export function hasIntervalData(facts: IntervalUsageFacts): boolean {
  return (
    facts.onPeakUsage > 0 ||
    facts.intermediateUsage > 0 ||
    facts.offPeakUsage > 0 ||
    facts.onPeakCost > 0 ||
    facts.intermediateCost > 0 ||
    facts.offPeakCost > 0
  );
}

/**
 * Re-syncs to data presence only when presence flips, so a user's manual
 * collapse/expand survives subsequent `updateComputedValues()` passes.
 */
export function resolveIntervalExpanded(
  prevHasData: boolean,
  prevExpanded: boolean,
  hasDataNow: boolean,
): boolean {
  return prevHasData === hasDataNow ? prevExpanded : hasDataNow;
}

export interface IntervalTotals {
  totalUsage: number;
  totalCost: number;
  /** Blended $/unit rate; 0 when totalUsage is 0. */
  blendedRate: number;
  /** False when totalUsage is 0 (nothing to reconcile). */
  usageMismatch: boolean;
}

export function computeIntervalTotals(
  facts: IntervalUsageFacts,
  usageAmount: number,
): IntervalTotals {
  const totalUsage = facts.onPeakUsage + facts.intermediateUsage + facts.offPeakUsage;
  const totalCost = facts.onPeakCost + facts.intermediateCost + facts.offPeakCost;
  return {
    totalUsage,
    totalCost,
    blendedRate: totalUsage > 0 ? totalCost / totalUsage : 0,
    usageMismatch: totalUsage > 0 && Math.abs(totalUsage - usageAmount) > INTERVAL_USAGE_TOLERANCE,
  };
}
